import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { IconButton } from './icon-button';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  maxBody?: number | string;
}

/** Bottom-sheet on mobile, centered dialog on desktop. Portaled to <body>. */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  closeLabel = 'Close',
  maxBody,
}: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="wb-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="wb-sheet" role="dialog" aria-modal="true" aria-label={title || 'Dialog'}>
        <div className="wb-sheet-grip" />
        {title && (
          <div className="wb-sheet-head">
            <div className="wb-h3 wb-grow">{title}</div>
            <IconButton name="close" label={closeLabel} onClick={onClose} />
          </div>
        )}
        <div className="wb-sheet-body" style={maxBody ? { maxHeight: maxBody } : undefined}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: '6px 18px 20px', borderTop: '1px solid var(--line)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
